import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useState, type ReactNode } from 'react';

import {
  ENROL_OPERATIONS,
  STEP_UP_ACTIONS,
  discardBiometricEnrolment,
  enrolBiometricDevice,
  listBiometricDevices,
  removeBiometricDevice,
  type BiometricDevice,
} from '../api/step-up.ts';
import { IconChevronLeft } from '../components/icons.tsx';
import { ErrorState, LoadingState } from '../components/states.tsx';
import { showToast } from '../components/toast.tsx';
import { suggestDeviceLabel } from '../lib/step-up.ts';
import { isStepUpCancelled, useStepUpGate } from '../lib/use-step-up.tsx';
import { usePlatform, type BiometricState } from '../platform/index.ts';

import { rootRoute } from './__root.tsx';

const formatDate = (iso: string): string => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
};

const describe = (device: BiometricDevice, isThisDevice: boolean): string => {
  const added = `Added ${formatDate(device.createdAt)}`;
  const used =
    device.lastUsedAt === null ? 'never used' : `last used ${formatDate(device.lastUsedAt)}`;
  return isThisDevice ? `${added} · ${used} · this device` : `${added} · ${used}`;
};

const SecurityPage = (): ReactNode => {
  const platform = usePlatform();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const stepUp = useStepUpGate();
  const [busy, setBusy] = useState(false);
  const [biometrics, setBiometrics] = useState<BiometricState | null>(null);

  // Asks the Telegram client what this device can do. Resolves to
  // "unavailable" on clients older than Bot API 7.2 and in a dev browser.
  useEffect(() => {
    let cancelled = false;
    void platform.biometricState().then((state) => {
      if (!cancelled) setBiometrics(state);
    });
    return (): void => {
      cancelled = true;
    };
  }, [platform]);

  const devicesQ = useQuery({
    queryKey: ['biometric-devices'],
    queryFn: ({ signal }) => listBiometricDevices(signal),
  });

  const refresh = (): void => {
    void qc.invalidateQueries({ queryKey: ['biometric-devices'] });
  };

  // Setting a device up is itself confirmed — otherwise anyone holding the
  // Telegram session could enrol their own device and satisfy every other
  // check. The token the bot mints goes straight into the device's secure
  // storage and is never held anywhere this page can read it back.
  const enrolM = useMutation({
    mutationFn: async (): Promise<void> => {
      const granted = await platform.biometricRequestAccess(
        'Confirm card details, PIN changes and Vault transfers',
      );
      if (!granted) throw new Error('Telegram did not grant access to biometrics.');
      const state = await platform.biometricState();
      setBiometrics(state);
      const { token } = await stepUp.guard(
        {
          action: STEP_UP_ACTIONS.SECOND_FACTOR_ENROL,
          params: { operation: ENROL_OPERATIONS.ENROL, deviceId: state.deviceId },
          summary: 'Set up this device to confirm sensitive actions.',
        },
        (grant) =>
          enrolBiometricDevice(state.deviceId, suggestDeviceLabel(navigator.userAgent), grant),
      );
      const saved = await platform.secureSet(token);
      if (!saved) {
        // The bot has a credential this device cannot answer for. Left there it
        // counts as a factor, and on an account with no email address that is
        // the end of every confirmation, including setting a device up again.
        await discardBiometricEnrolment(state.deviceId, token).catch(() => undefined);
        throw new Error('Telegram could not store the confirmation on this device.');
      }
    },
    onSuccess: () => {
      showToast('This device is set up');
      refresh();
    },
    onError: (err: unknown) => {
      if (isStepUpCancelled(err)) return;
      showToast(err instanceof Error ? err.message : 'Could not set up this device');
    },
    onSettled: () => {
      setBusy(false);
    },
  });

  const removeM = useMutation({
    mutationFn: (deviceId: string) =>
      stepUp.guard(
        {
          action: STEP_UP_ACTIONS.SECOND_FACTOR_ENROL,
          params: { operation: ENROL_OPERATIONS.REMOVE, deviceId },
          summary: 'Remove a device from this account.',
        },
        (grant) => removeBiometricDevice(deviceId, grant),
      ),
    onSuccess: () => {
      showToast('Device removed');
      refresh();
    },
    onError: (err: unknown) => {
      if (isStepUpCancelled(err)) return;
      showToast(err instanceof Error ? err.message : 'Could not remove that device');
    },
    onSettled: () => {
      setBusy(false);
    },
  });

  const devices = devicesQ.data?.devices ?? [];
  const thisDeviceId = biometrics?.deviceId ?? '';
  const thisDeviceEnrolled = devices.some(
    (d) => d.deviceId === thisDeviceId && thisDeviceId !== '',
  );

  return (
    <main className="screen">
      <header className="topbar">
        <button
          type="button"
          className="topbar-back"
          aria-label="Back"
          onClick={(): void => {
            void navigate({ to: '/account' });
          }}
        >
          <IconChevronLeft />
        </button>
        <h1 className="topbar-title">Security</h1>
        <span className="topbar-spacer" aria-hidden="true" />
      </header>

      <section className="security-section">
        <p className="security-copy">
          Keep your account yours. Let this device confirm sensitive actions.
        </p>

        {devicesQ.isLoading ? <LoadingState label="Loading devices" /> : null}
        {devicesQ.isError ? (
          <ErrorState
            message="Could not load your devices"
            onRetry={(): void => {
              void devicesQ.refetch();
            }}
          />
        ) : null}

        {!devicesQ.isLoading && !devicesQ.isError && devices.length === 0 ? (
          <p className="security-copy">
            No device is set up yet. Until one is, these actions fall back to a code emailed to you.
          </p>
        ) : null}

        <ul className="passkey-list">
          {devices.map((device) => (
            <li key={device.deviceId} className="passkey-row">
              <div>
                <p className="passkey-row-name">{device.label ?? 'Device'}</p>
                <p className="passkey-row-meta">
                  {describe(device, device.deviceId === thisDeviceId)}
                </p>
              </div>
              <button
                type="button"
                className="link-button"
                disabled={busy}
                onClick={(): void => {
                  setBusy(true);
                  removeM.mutate(device.deviceId);
                }}
              >
                Remove
              </button>
            </li>
          ))}
        </ul>

        {biometrics !== null && !biometrics.available ? (
          <p className="security-copy">
            This Telegram client cannot check a fingerprint or face, so confirmations here go to
            your email. Opening the Mini App on a phone lets you set that device up.
          </p>
        ) : null}

        {biometrics?.available === true && !thisDeviceEnrolled ? (
          <div className="security-actions">
            <button
              type="button"
              className="btn-primary"
              disabled={busy}
              onClick={(): void => {
                setBusy(true);
                enrolM.mutate();
              }}
            >
              {busy ? 'Working…' : 'Set up'}
            </button>
          </div>
        ) : null}
      </section>

      {stepUp.sheet}
    </main>
  );
};

export const securityRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/security',
  component: SecurityPage,
});
